import React from 'react';
import { cn } from '../../../lib/cn';
import type { IconKind } from '../../Icon/Icon';
import { Icon } from '../../Icon/Icon';
import { SettingsToggle } from './SettingsToggle';

type GeneralInfoSectionCardProps = {
  title: string;
  subtitle?: string;
  eyebrow?: string;
  icon?: IconKind;
  smart?: boolean;
  headerRight?: React.ReactNode;
  toggle?: { value: boolean; onChange: (value: boolean) => void };
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
};

export function GeneralInfoSectionCard({
  title,
  subtitle,
  eyebrow,
  icon,
  smart,
  headerRight,
  toggle,
  children,
  className,
  bodyClassName,
}: GeneralInfoSectionCardProps) {
  const collapsed = toggle && !toggle.value;

  return (
    <section
      className={cn(
        'overflow-hidden rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]',
        className,
      )}
    >
      {smart ? <div className="h-[3px] bg-[var(--grad-spectrum)]" /> : null}
      <header
        className={cn(
          'flex flex-wrap items-start gap-x-3.5 gap-y-3 px-[22px] py-[18px]',
          !collapsed && 'border-b border-[var(--cream-2)]',
        )}
      >
        {icon ? (
          <span
            className={cn(
              'mt-px inline-flex size-[34px] shrink-0 items-center justify-center rounded-[10px]',
              smart ? 'bg-[var(--ai-tint)] text-[var(--ai-3)]' : 'bg-[var(--surface-mute)] text-[var(--ink-2)]',
            )}
          >
            <Icon kind={icon} size={17} stroke={1.8} />
          </span>
        ) : null}
        <div className="min-w-[200px] flex-1">
          {eyebrow ? (
            <div className="mb-1.5 inline-flex items-center gap-1.5 font-[var(--font-mono)] text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--ink-3)]">
              {smart ? <Icon kind="sparkle" size={12} stroke={2} className="text-[var(--ai-3)]" /> : null}
              {eyebrow}
            </div>
          ) : null}
          <h2 className="m-0 font-[var(--font-display)] text-lg font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
            {title}
          </h2>
          {subtitle ? (
            <p className="mt-1.5 max-w-[600px] text-[13px] leading-normal text-[var(--ink-2)]">{subtitle}</p>
          ) : null}
        </div>
        <div className="ml-auto flex shrink-0 flex-wrap items-center gap-2.5">
          {headerRight}
          {toggle ? (
            <>
              <span
                className={cn(
                  'text-xs font-semibold',
                  toggle.value ? 'text-[var(--success)]' : 'text-[var(--ink-3)]',
                )}
              >
                {toggle.value ? 'On' : 'Off'}
              </span>
              <SettingsToggle checked={toggle.value} onChange={toggle.onChange} />
            </>
          ) : null}
        </div>
      </header>
      {!collapsed ? <div className={cn('p-[22px]', bodyClassName)}>{children}</div> : null}
    </section>
  );
}
